import { useCallback, useEffect, useMemo } from 'react';

import { useChannelList } from '@/lib/api/orval';
import type { ChannelList } from '@/lib/api/orval';
import { useUIStore } from '@/lib/stores/uiStore';

// ============================================================================
// Types
// ============================================================================

export interface UseChannelContextReturn {
  currentChannel: ChannelList | null;
  currentChannelId: string | null;
  channels: ChannelList[];
  isLoading: boolean;
  hasNoChannel: boolean;
  isSingleChannel: boolean;
  isMultiChannel: boolean;
  setChannel: (id: string | null) => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * 채널 컨텍스트 훅
 *
 * 선택된 채널 상태를 중앙 관리.
 * - uiStore.selectedChannelId (persist) 기반
 * - 자동 선택: 1채널이면 자동 set, is_default 우선, stale ID 리셋
 */
export function useChannelContext(): UseChannelContextReturn {
  const { data: channelData, isLoading } = useChannelList();
  const selectedChannelId = useUIStore((s) => s.selectedChannelId);
  const setSelectedChannelId = useUIStore((s) => s.setSelectedChannelId);

  const channels: ChannelList[] = useMemo(
    () => channelData?.results ?? [],
    [channelData?.results]
  );

  // 자동 선택 로직
  useEffect(() => {
    if (isLoading || channels.length === 0) {return;}

    // stale ID 체크: 저장된 ID가 현재 목록에 없으면 리셋
    if (selectedChannelId !== null) {
      const exists = channels.some((ch) => ch.id === selectedChannelId);
      if (!exists) {
        setSelectedChannelId(null);
      }
      return;
    }

    // selectedChannelId가 null인 경우 자동 선택
    // 1채널: 그 채널로 자동 set
    if (channels.length === 1) {
      setSelectedChannelId(channels[0].id);
      return;
    }

    // 다채널: is_default 채널 우선, 없으면 첫 번째
    const defaultChannel = channels.find((ch) => ch.isDefault);
    if (defaultChannel) {
      setSelectedChannelId(defaultChannel.id);
    } else {
      setSelectedChannelId(channels[0].id);
    }
  }, [channels, isLoading, selectedChannelId, setSelectedChannelId]);

  const currentChannel =
    selectedChannelId !== null
      ? (channels.find((ch) => ch.id === selectedChannelId) ?? null)
      : null;

  const setChannel = useCallback(
    (id: string | null) => {
      setSelectedChannelId(id);
    },
    [setSelectedChannelId]
  );

  return {
    currentChannel,
    currentChannelId: selectedChannelId,
    channels,
    isLoading,
    hasNoChannel: !isLoading && channels.length === 0,
    isSingleChannel: !isLoading && channels.length === 1,
    isMultiChannel: !isLoading && channels.length >= 2,
    setChannel,
  };
}

// ============================================================================
// Helper Hook
// ============================================================================

/**
 * 메뉴 링크에 ?channel=xxx 쿼리 자동 부착 헬퍼
 *
 * @param href 기본 경로 (예: '/playlists')
 * @param channelId 채널 ID (null이면 쿼리 미부착)
 * @returns 채널 ID가 있으면 '/playlists?channel=xxx', 없으면 '/playlists'
 */
export function makeChannelHref(href: string, channelId: string | null): string {
  if (!channelId) {return href;}
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}channel=${channelId}`;
}
